import { WorkpaperProcedureCategory, WorkpaperProcedureResult, WorkpaperProceduresGroup } from '../../types/workpaper-procedure';

/**
 * Categorise and sort workpaper procedures
 * @param categories Array of all workpaper procedure categories
 * @param workpaperProcedures Array of workpaper procedures with category id
 * @param collapsedCategories Ids of collapsed categories
 * @returns Categorised procedure groups, and non-categorised procedure group
 */
export function categoriseProcedures(
    categories: WorkpaperProcedureCategory[],
    workpaperProcedures: WorkpaperProcedureResult[],
    collapsedCategories: string[]
): { categoryGroups: WorkpaperProceduresGroup[]; otherProceduresGroup: WorkpaperProceduresGroup } {
    const categoryGroups: WorkpaperProceduresGroup[] = [];

    // Get all categories besides 'others', and sort them alphabetically
    const sortedCategories = categories.filter((c) => c.id !== null && c.name !== null).sort((a, b) => (a.name! > b.name! ? 1 : -1));

    // Loop through each unique category, group procedures that belong in that category
    for (const category of sortedCategories) {
        const procedures = workpaperProcedures.filter((wp) => wp.category?.id === category.id);
        if (procedures.length > 0) {
            // Sort procedures by sequence
            const sortedProcedures = procedures.map((p) => p.procedure).sort((a, b) => (a.sequence > b.sequence ? 1 : -1));
            categoryGroups.push({
                categoryId: category.id as string,
                categoryName: category.name as string,
                totalProcedureCount: category.totalProcedureCount,
                completedProcedureCount: category.completedProcedureCount,
                procedures: sortedProcedures,
                isCollapsed: collapsedCategories.includes(category.id as string),
            });
        }
    }

    // Get all remaining procedures with null category
    const otherProceduresGroup: WorkpaperProceduresGroup = {
        categoryId: undefined,
        categoryName: 'Other',
        procedures: workpaperProcedures
            .filter((wp) => wp.category === undefined || wp.category === null)
            .map((op) => op.procedure)
            .sort((a, b) => (a.sequence > b.sequence ? 1 : -1)),
        isCollapsed: collapsedCategories.includes('other'),
        totalProcedureCount: categories.find((c) => c.id === null)?.totalProcedureCount ?? 0,
        completedProcedureCount: categories.find((c) => c.id === null)?.completedProcedureCount ?? 0,
    };

    return { categoryGroups, otherProceduresGroup };
}

/**
 * Returns the id of the first procedure given all categories of procedures
 * @param categoryGroups Array of categorised procedures
 * @param otherProceduresGroup Array of uncategorised procedures
 * @returns First procedure id or undefined
 */
export function getFirstProcedureId(
    categoryGroups: WorkpaperProceduresGroup[],
    otherProceduresGroup: WorkpaperProceduresGroup
): string | undefined {
    if (categoryGroups.length > 0 && categoryGroups[0].procedures.length > 0) {
        return categoryGroups[0].procedures[0].id;
    }
    if (otherProceduresGroup.procedures.length > 0) {
        return otherProceduresGroup.procedures[0].id;
    }
}
